'use client';

import { useQuery } from '@tanstack/react-query';
import { playerQueryOptions } from '@/queries/player/player.queries';
import { useAppSelector } from '@/redux/hooks';

export interface GmBarProgressData {
  currentLevelXpRequired: number;
  level: number;
  nextLevelXpRequired: number;
  percent: number;
  totalXp: number;
}

const getProgressPercent = (totalXp: number, currentLevelXpRequired: number, nextLevelXpRequired: number) => {
  const levelXpRange = nextLevelXpRequired - currentLevelXpRequired;

  if (levelXpRange <= 0) {
    return 0;
  }

  const currentLevelXp = totalXp - currentLevelXpRequired;

  return Math.max(0, Math.min(100, (currentLevelXp / levelXpRange) * 100));
};

export const useGmBarPlayerData = () => {
  const userSession = useAppSelector(state => state.user.userSession);
  const playerProfileQuery = useQuery(playerQueryOptions.mePlayerProfile({ enabled: !!userSession }));
  const playerProfile = playerProfileQuery.data;

  const nextLevelXpRequired = playerProfile?.nextLevelXpRequired ?? playerProfile?.currentLevelXpRequired ?? 0;
  const progress: GmBarProgressData | null = playerProfile
    ? {
        currentLevelXpRequired: playerProfile.currentLevelXpRequired,
        level: playerProfile.level,
        nextLevelXpRequired,
        percent: getProgressPercent(playerProfile.totalXp, playerProfile.currentLevelXpRequired, nextLevelXpRequired),
        totalXp: playerProfile.totalXp,
      }
    : null;

  return {
    isError: playerProfileQuery.isError,
    isLoading: playerProfileQuery.isLoading,
    playerProfile,
    progress,
  };
};
